import { useMemo } from "react";
import { StateMessage } from "../components/state-message";
import {
  formatEventDateRangeShort,
  formatEventTimeRange,
  viewerTimeZone,
} from "../lib/date";
import { buildMonthGrid } from "../lib/month-grid";
import type { ViewProps } from "./types";

const SUN_LABELS = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];
const MON_LABELS = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];

export function MonthlyView({
  config,
  events,
  isLoading,
  errorMessage,
  anchorDate,
  onSelect,
}: ViewProps) {
  const weekStart = config.monthlyWeekStart;

  const weeks = useMemo(
    () => buildMonthGrid(anchorDate, events, weekStart),
    [anchorDate, events, weekStart],
  );

  const monthLabel = useMemo(
    () =>
      new Intl.DateTimeFormat(undefined, {
        timeZone: viewerTimeZone(),
        month: "long",
        year: "numeric",
      }).format(anchorDate),
    [anchorDate],
  );

  if (isLoading) {
    return <StateMessage>Loading events…</StateMessage>;
  }
  if (errorMessage) {
    return (
      <StateMessage variant="error">
        Unable to load events: {errorMessage}
      </StateMessage>
    );
  }

  const dayLabels = weekStart === "sun" ? SUN_LABELS : MON_LABELS;

  return (
    <div className="gitg-monthly">
      <h3 className="gitg-month-label">{monthLabel}</h3>
      <div className="gitg-month-weekdays">
        {dayLabels.map((label) => (
          <div key={label} className="gitg-month-weekday">
            {label}
          </div>
        ))}
      </div>
      <div className="gitg-month-grid">
        {weeks.map((week, weekIdx) => (
          <div
            key={weekIdx}
            className="gitg-month-week"
            style={{
              gridTemplateRows:
                week.spanRowCount > 0
                  ? `auto repeat(${week.spanRowCount}, auto) minmax(var(--gitg-month-day-min-h, 90px), 1fr)`
                  : `auto minmax(var(--gitg-month-day-min-h, 90px), 1fr)`,
            }}
          >
            {/* Column dividers: 6 thin vertical lines between the 7 columns,
                spanning every row of the week. */}
            {[1, 2, 3, 4, 5, 6].map((colIdx) => (
              <div
                key={`vdiv-${colIdx}`}
                className="gitg-month-vdivider"
                aria-hidden="true"
                style={{ gridColumn: colIdx + 1, gridRow: "1 / -1" }}
              />
            ))}

            {/* Top row: day numbers. */}
            {week.days.map((day, dayIdx) => (
              <div
                key={`num-${day.dayKey}`}
                className={
                  "gitg-month-day-num" +
                  (day.isCurrentMonth ? "" : " gitg-month-day-num--out") +
                  (day.isToday ? " gitg-month-day-num--today" : "")
                }
                style={{ gridColumn: dayIdx + 1, gridRow: 1 }}
              >
                {day.date.getDate()}
              </div>
            ))}

            {/* Multi-day ribbons. Each span sits in its own row, between the
                day-num row and the day-content row, covering startCol..endCol. */}
            {week.spans.map((span, spanIdx) => (
              <button
                key={`${span.event.id}-${spanIdx}`}
                type="button"
                aria-label={span.event.name}
                className={
                  "gitg-month-event-span" +
                  (span.isStart ? " gitg-month-event-span--start" : "") +
                  (span.isEnd ? " gitg-month-event-span--end" : "")
                }
                style={{
                  gridColumn: `${span.startCol + 1} / ${span.endCol + 2}`,
                  gridRow: span.row + 2,
                }}
                onClick={() => onSelect(span.event.id)}
              >
                {span.isStart ? (
                  <>
                    <div className="gitg-month-event-span-date">
                      {formatEventDateRangeShort(span.event)}
                    </div>
                    <div className="gitg-month-event-span-name">
                      {span.event.name}
                    </div>
                  </>
                ) : null}
              </button>
            ))}

            {/* Bottom row: per-day single-day events. */}
            {week.days.map((day, dayIdx) => (
              <div
                key={`day-${day.dayKey}`}
                className={
                  "gitg-month-day" +
                  (day.isCurrentMonth ? "" : " gitg-month-day--out") +
                  (day.isToday ? " gitg-month-day--today" : "")
                }
                style={{
                  gridColumn: dayIdx + 1,
                  gridRow: week.spanRowCount + 2,
                }}
              >
                {day.singleDayEvents.map((event) => (
                  <button
                    key={event.id}
                    type="button"
                    className="gitg-month-event"
                    onClick={() => onSelect(event.id)}
                  >
                    {event.image ? (
                      <img
                        className="gitg-month-event-image"
                        src={event.image}
                        alt=""
                        aria-hidden="true"
                      />
                    ) : null}
                    <div className="gitg-month-event-time">
                      {formatEventTimeRange(event)}
                    </div>
                    <div className="gitg-month-event-name">{event.name}</div>
                  </button>
                ))}
              </div>
            ))}
          </div>
        ))}
      </div>
      {events.length === 0 ? (
        <StateMessage>No events this month.</StateMessage>
      ) : null}
    </div>
  );
}
